import noop from "lodash/noop";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useManageRolesStoreActions, useManageRolesStoreContext } from "PFApp/stores/manage_roles";
import { PendingEdit } from "PFApp/stores/manage_roles/manage_roles_store.types";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { updateActivity } from "PFCore/services/activities";
import { Id } from "PFCore/types";
import { useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { buildRoleMap, findRoleFormPath } from "../manage_roles.utils";
import {
  buildRolePayload,
  getPendingEditsWithStatus,
  groupEditsByRole
} from "./use_pending_edits_sync.utils";

const DEBOUNCE_MS = 1000;
const MAX_WAIT_MS = 5000;

export const MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID = getGrowlId("manage-roles-sync-success");
export const MANAGE_ROLES_SYNC_ERROR_GROWL_ID = getGrowlId("manage-roles-sync-error");

export const usePendingEditsSync = () => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });

  const { getValues, setValue } = useFormContext<ManageRolesMultiForm>();
  const { markInflight, clearEdits, revertEdit } = useManageRolesStoreActions();
  const storeApi = useManageRolesStoreContext();
  const { showGrowl } = useGrowlStoreActions();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxWaitTimerRef = useRef<NodeJS.Timeout | null>(null);
  // scheduleSyncRef breaks a circular dependency: runSync calls scheduleSyncRef.current (to reschedule
  // after partial syncs) and scheduleSync calls runSync (to trigger the actual sync). Defining both as
  // useCallback would create a dependency cycle that React cannot resolve. The ref is updated each render
  // via the useEffect below so it always holds the latest scheduleSync without being a direct dependency.
  const scheduleSyncRef = useRef<() => void>(noop);
  const syncPausedRef = useRef<boolean>(false);

  const cancelTimers = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (maxWaitTimerRef.current !== null) {
      clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = null;
    }
  }, []);

  const rollbackRole = useCallback(
    (roleId: Id, edits: PendingEdit[], keys: string[]) => {
      const path = findRoleFormPath(getValues(), roleId);
      if (!path) {
        keys.forEach((key) => revertEdit(key));
        return;
      }
      const { subtemplateIndex, roleIndex } = path;
      edits.forEach((edit) => {
        setValue(
          `subtemplates.${subtemplateIndex}.roles.${roleIndex}.${edit.fieldName}` as any,
          edit.previousValue as any
        );
        revertEdit(`${roleId}.${edit.fieldName}`);
      });
    },
    [getValues, revertEdit, setValue]
  );

  const syncOneRole = useCallback(
    async (roleId: Id, keys: string[], edits: PendingEdit[], roleValues: ManageRolesFormType) => {
      const payload = buildRolePayload(roleValues, edits);

      if (Object.keys(payload).length <= 1 && "id" in payload) {
        clearEdits(keys);
        return;
      }

      try {
        await updateActivity(roleId, payload);
        clearEdits(keys);
      } catch {
        rollbackRole(roleId, edits, keys);
        throw new Error(`Role ${roleId} update failed`);
      }
    },
    [clearEdits, rollbackRole]
  );

  const performSync = useCallback(async (): Promise<boolean> => {
    const pendingOnly = getPendingEditsWithStatus(storeApi.getState().pendingEdits, "pending");

    const editKeys = Object.keys(pendingOnly);
    if (editKeys.length === 0) {
      return true;
    }

    markInflight(editKeys);

    const roleMap = buildRoleMap(getValues());
    const byRole = groupEditsByRole(pendingOnly);
    const results = await Promise.allSettled(
      Array.from(byRole.entries()).map(([roleId, { keys, edits }]) => {
        const roleValues = roleMap.get(roleId);
        if (!roleValues) {
          clearEdits(keys);
          return Promise.resolve();
        }
        return syncOneRole(roleId, keys, edits, roleValues);
      })
    );

    const failuresCount = results.filter((result) => result.status === "rejected").length;
    const successesCount = byRole.size - failuresCount;

    if (successesCount > 0) {
      showGrowl({
        message: t("saveSuccess", { count: successesCount }),
        kind: "success",
        id: MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID,
        hidePreviousByTypeAndId: true
      });
    }
    if (failuresCount > 0) {
      showGrowl({
        message: t("saveError"),
        kind: "error",
        id: MANAGE_ROLES_SYNC_ERROR_GROWL_ID,
        hidePreviousByTypeAndId: true
      });
    }

    return failuresCount === 0;
  }, [clearEdits, getValues, showGrowl, markInflight, storeApi, syncOneRole, t]);

  const runSync = useCallback(async () => {
    cancelTimers();
    await performSync();
    const stillPending = Object.values(storeApi.getState().pendingEdits).some(
      (edit) => edit.status === "pending"
    );
    if (stillPending) {
      scheduleSyncRef.current();
    }
  }, [cancelTimers, performSync, storeApi]);

  const scheduleSync = useCallback(() => {
    if (syncPausedRef.current) {
      return;
    }
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(
      () => runSync().catch((err) => console.error("Unexpected sync error:", err)),
      DEBOUNCE_MS
    );
    if (maxWaitTimerRef.current === null) {
      maxWaitTimerRef.current = setTimeout(
        () => runSync().catch((err) => console.error("Unexpected sync error:", err)),
        MAX_WAIT_MS
      );
    }
  }, [runSync]);

  useEffect(() => {
    scheduleSyncRef.current = scheduleSync;
  }, [scheduleSync]);

  useEffect(() => {
    let prevPendingEdits = storeApi.getState().pendingEdits;
    const unsubscribe = storeApi.subscribe((state) => {
      if (state.pendingEdits !== prevPendingEdits) {
        prevPendingEdits = state.pendingEdits;
        scheduleSync();
      }
    });
    return () => {
      unsubscribe();
      cancelTimers();
    };
  }, [cancelTimers, scheduleSync, storeApi]);

  const flushPendingEdits = useCallback(async (): Promise<boolean> => {
    cancelTimers();
    return performSync();
  }, [cancelTimers, performSync]);

  const pauseSync = useCallback(() => {
    cancelTimers();
    syncPausedRef.current = true;
  }, [cancelTimers]);

  const resumeSync = useCallback(() => {
    syncPausedRef.current = false;

    const hasPending = Object.values(storeApi.getState().pendingEdits).some(
      (edit) => edit.status === "pending"
    );

    if (hasPending) {
      scheduleSync();
    }
  }, [scheduleSync, storeApi]);

  return { flushPendingEdits, pauseSync, resumeSync };
};
